<template>
  <div>
    <table-layout
      pkField="pipeline_id"
      :selected.sync="selected"
      :isAdmin="isAdmin"
      :loading="serversLoading"
      :items="pipelines"
      :columns="columns"
      :addOptions="['addPipeline']"
      @click:addNew="addPipeline"
    >
      <template #actions>
        <b-dropdown-item @click="runPipelines" aria-role="listitem">{{ $t('server.actions.run') }}</b-dropdown-item>
        <b-dropdown-item @click="openDialogStop" aria-role="listitem">{{ $t('server.actions.stop') }}</b-dropdown-item>
        <b-dropdown-item @click="openDialogRemove" aria-role="listitem">{{ $t('lists.deleteDetail') }}</b-dropdown-item>
      </template>

      <template #status="props">
        <pipeline-status-indicator :status="props.value.status"></pipeline-status-indicator>
      </template>
    </table-layout>
    <b-modal
      :active.sync="isAddPipeline"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
      class="modal-layout"
    >
      <div class="modal-card" style="width: auto">
        <section class="modal-card-body">
          <h1>{{ $t('server.pipelines.add_new') }}</h1>
          <parametrs-list :items.sync="newPipeline" :parameters="newPipelineFields"> </parametrs-list>
          <edit-button v-if="!isAddPipelineLoading" :editOptions="['save']" @click="savePipeline">
            <template>
              <span class="text">{{ $t('button.save') }}</span>
            </template>
          </edit-button>
          <loader v-else></loader>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex'
import TableLayout from '@/layout/TableLayout'
import ParametrsList from '@/components/ParametrsList'
import EditButton from '@/components/EditButton'
import Loader from '@/components/Loader'
import PipelineStatusIndicator from '@/components/PipelineStatusIndicator'
const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'Pipelines',
  components: {
    Loader,
    TableLayout,
    EditButton,
    ParametrsList,
    PipelineStatusIndicator
  },
  created() {
    this.refetch()
  },
  computed: {
    ...mapUserState({
      role: state => state.user.role
    }),
    isAdmin() {
      return this.role === 'ADMIN'
    }
  },
  data() {
    return {
      pipelines: [],
      serversLoading: false,
      isAddPipeline: false,
      isAddPipelineLoading: false,
      newPipelineFields: [],
      newPipeline: {},
      columns: [
        {
          sortable: true,
          field: 'name',
          label: 'columns.pipeline.name',
          route: {
            name: 'pipeline',
            params: [
              {
                name: 'pipeline_id',
                field: 'pipeline_id'
              }
            ]
          }
        },
        {
          slot: 'status',
          label: 'columns.status'
        },
        {
          sortable: true,
          field: 'description',
          label: 'columns.description',
          bindHtml: true
        },
        {
          sortable: true,
          field: 'created_time',
          label: 'columns.created_time'
        },
        {
          sortable: true,
          field: 'modified_time',
          label: 'columns.modified_time'
        }
      ],
      selected: []
    }
  },
  methods: {
    ...mapActions('general', ['setServerName']),
    addPipeline() {
      this.isAddPipeline = true
    },
    refetch() {
      if (!this.serversLoading) {
        const body = {
          server_id: this.$route.params.server_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines?${searchParams}`
        this.serversLoading = true
        fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.pipelines = data.pipelines
            this.newPipelineFields = data.new_pipelines_fields
            this.newPipelineFields.forEach(element => {
              this.newPipeline[element.name] = element.default_value
            })

            this.setServerName(data.server_name)
            this.refetchStatus()
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    refetchStatus() {
      const body = {
        server_id: this.$route.params.server_id
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines/status?${searchParams}`
      fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.pipelines = this.pipelines.map(d => {
            d.status = data[d.pipeline_id]
            return d
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
      if (this.$route.name == 'pipelines') {
        setTimeout(this.refetchStatus, 5000)
      }
    },
    savePipeline() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines`
      this.isAddPipelineLoading = true
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_config: this.newPipeline
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(pipeline_id => {
          const pipeline = Object.assign({}, this.newPipeline)
          pipeline.pipeline_id = pipeline_id
          pipeline.last_status = 'stop'
          this.pipelines.push(pipeline)
          this.isAddPipeline = false
          this.isAddPipelineLoading = false
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    deletePipelines(pipeline_ids) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines/delete`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_ids: pipeline_ids
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          const indexSet = new Set(pipeline_ids)
          this.pipelines = this.pipelines.filter(value => {
            return !indexSet.has(value.pipeline_id)
          })
          this.selected = []
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    openDialogRemove() {
      if (!this.selected.length) {
        this.$notify({
          message: this.$t('notification.unselected'),
          type: 'danger'
        })
        return
      }
      const indexSet = new Set(this.selected)
      const pipelines = this.pipelines
        .filter(value => {
          return indexSet.has(value.pipeline_id)
        })
        .map(d => {
          return d['name']
        })
      const msg = pipelines
        .map(d => {
          return d
        })
        .join(', ')

      this.$dialog.confirm({
        message: `${this.$t('confirm.remove', {
          msg: msg
        })}?`,
        onConfirm: () => {
          this.serversLoading = true
          this.deletePipelines(this.selected)
            .then(() => {
              this.$notify({
                message: this.$t('notification.removed', {
                  msg: msg
                }),
                type: 'success'
              })
            })
            .finally(() => (this.serversLoading = false))
        }
      })
    },

    openDialogStop(pipeline_id = undefined) {
      if (!this.selected.length && pipeline_id === undefined) {
        this.$notify({
          message: this.$t('notification.unselected'),
          type: 'danger'
        })
        return
      }

      let pipeline_ids = []
      if (pipeline_id !== undefined) {
        pipeline_ids.push(pipeline_id)
      } else {
        pipeline_ids = this.selected
      }
      let indexSet = new Set(pipeline_ids)

      const pipelines = this.pipelines
        .filter(value => {
          return indexSet.has(value.pipeline_id)
        })
        .map(d => {
          return d['name']
        })
      const msg = pipelines
        .map(d => {
          return d
        })
        .join(', ')
      this.$dialog.confirm({
        message: `${this.$t('confirm.stop', {
          msg: msg
        })}?`,
        onConfirm: () => {
          this.pipelines = this.pipelines.map(value => {
            if (indexSet.has(value.pipeline_id)) {
              value.status = 'processing'
            }
            return value
          })
          this.stopPipelines(pipeline_ids)
            .then(res => {
              if (!res.ok) {
                throw res.json()
              }
              return res.json()
            })
            .then(() => {
              this.notifyPipelines({
                name: msg,
                status: 'stopped'
              })
            })
            .catch(error =>
              error.then(data => {
                this.$notify({
                  message: JSON.parse(data.message).message,
                  type: 'danger'
                })
              })
            )
            .finally(() => {
              this.pipelinesLoading = false
            })
        }
      })
    },
    runPipelines(pipeline_id = undefined) {
      if (!this.selected.length && pipeline_id === undefined) {
        this.$notify({
          message: this.$t('notification.unselected'),
          type: 'danger'
        })
        return
      }

      let pipeline_ids = []
      if (pipeline_id !== undefined) {
        pipeline_ids.push(pipeline_id)
      } else {
        pipeline_ids = this.selected
      }
      let indexSet = new Set(pipeline_ids)
      const pipelines = this.pipelines
        .filter(value => {
          return indexSet.has(value.pipeline_id)
        })
        .map(d => {
          return d['name']
        })
      const msg = pipelines
        .map(d => {
          return d
        })
        .join(', ')

      this.pipelines = this.pipelines.map(value => {
        if (indexSet.has(value.pipeline_id)) {
          value.status = 'processing'
        }
        return value
      })
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines/start`
      fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_ids: pipeline_ids
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.notifyPipelines({
            name: msg,
            status: 'running'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    stopPipelines(pipeline_ids) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/pipelines/stop`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.params.server_id,
          pipeline_ids: pipeline_ids
        }),
        method: 'POST'
      })
    },
    notifyPipelines(variables) {
      this.$notify({
        message: `${this.$t('notification.pipelineWas', {
          pipeline: variables.name,
          status: this.$t(`notification.server.${variables.status}`)
        })}.`,
        type: 'success'
      })
    }
  }
}
</script>

<style scoped lang="scss">
.table {
  width: 100%;
}
</style>
